/**
 * My Prompts Management Dialog
 * 
 * Dialog for users to create, edit, and archive their personal prompts.
 */

import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Archive, Loader2, X, Eye, ChevronDown, RotateCcw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import {
  useMyTemplates,
  useCreateMyTemplate,
  useUpdateMyTemplate,
  useArchiveMyTemplate,
  usePromptVariables,
  substituteVariables,
  extractProfileValues,
  type UserTemplate,
  type PromptVariable,
} from '@/hooks/usePromptLibrary';
import { cn } from '@/lib/utils';

interface MyPromptsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type DialogMode = 'list' | 'create' | 'edit';

interface FormData {
  title: string;
  description: string;
  bodyTemplate: string;
  categoryId: number | null;
}

const initialFormData: FormData = {
  title: '',
  description: '',
  bodyTemplate: '',
  categoryId: null,
};

export function MyPromptsDialog({ open, onOpenChange }: MyPromptsDialogProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<DialogMode>('list');
  const [editingTemplate, setEditingTemplate] = useState<UserTemplate | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [archiveConfirmId, setArchiveConfirmId] = useState<number | null>(null);
  const [showVariables, setShowVariables] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { data: templatesData, isLoading: isLoadingTemplates } = useMyTemplates(user?.id);
  const { data: variablesData } = usePromptVariables();
  const createMutation = useCreateMyTemplate();
  const updateMutation = useUpdateMyTemplate();
  const archiveMutation = useArchiveMyTemplate();

  // Fetch categories for dropdown
  const { data: categoriesData } = useQuery({
    queryKey: ['prompt-categories'],
    queryFn: async () => {
      // apiRequest already parses JSON and returns the data
      const data = await apiRequest<{
        categories?: Array<{ id: number | null; label: string }>;
      }>('GET', '/api/prompt-library/effective-templates');

      // Only surface categories with a concrete id; null/uncategorized is not selectable here
      return (
        data.categories
          ?.filter((c) => c.id !== null)
          .map((c) => ({ id: c.id as number, label: c.label })) || []
      );
    },
    staleTime: 10 * 60 * 1000,
  });

  // Fetch user profile for preview
  const { data: profileData } = useQuery({
    queryKey: ['/api/profile'],
    queryFn: () => apiRequest('GET', '/api/profile'),
    enabled: showPreview,
  });

  const profileValues = extractProfileValues(user, profileData);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setMode('list');
      setEditingTemplate(null);
      setFormData(initialFormData);
      setShowVariables(false);
      setShowPreview(false);
    }
  }, [open]);

  const handleCreate = () => {
    setMode('create');
    setFormData(initialFormData);
  };

  const handleEdit = (template: UserTemplate) => {
    setMode('edit');
    setEditingTemplate(template);
    setFormData({
      title: template.title,
      description: template.description || '',
      bodyTemplate: template.bodyTemplate,
      categoryId: template.categoryId,
    });
  };

  const handleArchive = async (id: number) => {
    try {
      await archiveMutation.mutateAsync(id);
      toast({
        title: 'Prompt archived',
        description: 'Your prompt has been archived.',
      });
      // Keep Admin view in sync if open
      queryClient.invalidateQueries({ queryKey: ['/api/admin/prompt-library/templates'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/prompt-library/categories'] });
      setArchiveConfirmId(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to archive prompt. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleUnarchive = async (id: number) => {
    try {
      await updateMutation.mutateAsync({
        id,
        data: { isActive: true },
      });
      toast({
        title: 'Prompt unarchived',
        description: 'Your prompt is active again.',
      });
      // Keep Admin view in sync if open
      queryClient.invalidateQueries({ queryKey: ['/api/admin/prompt-library/templates'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/prompt-library/categories'] });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to unarchive prompt. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.bodyTemplate.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Title and prompt body are required.',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync({
          title: formData.title.trim(),
          description: formData.description.trim() || undefined,
          bodyTemplate: formData.bodyTemplate,
          categoryId: formData.categoryId ?? undefined,
        });
        toast({
          title: 'Prompt created',
          description: 'Your new prompt has been saved.',
        });
      } else if (mode === 'edit' && editingTemplate) {
        await updateMutation.mutateAsync({
          id: editingTemplate.id,
          data: {
            title: formData.title.trim(),
            description: formData.description.trim() || undefined,
            bodyTemplate: formData.bodyTemplate,
            categoryId: formData.categoryId ?? undefined,
          },
        });
        toast({
          title: 'Prompt updated',
          description: 'Your prompt has been updated.',
        });
      }
      setMode('list');
      setEditingTemplate(null);
      setFormData(initialFormData);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save prompt. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const insertVariable = (key: string) => {
    const insertion = `{{${key}}}`;
    setFormData(prev => ({
      ...prev,
      bodyTemplate: prev.bodyTemplate + insertion,
    }));
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const renderList = () => (
    <>
      <DialogHeader>
        <DialogTitle>My Prompts</DialogTitle>
      </DialogHeader>
      
      <div className="py-4">
        {isLoadingTemplates ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : templatesData?.items && templatesData.items.length > 0 ? (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-2">
              {templatesData.items.map((template) => (
                <div
                  key={template.id}
                  className="border rounded-lg p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-gray-900 truncate">
                        {template.title}
                      </h4>
                      {template.description && (
                        <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                          {template.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {template.category && (
                          <Badge variant="secondary" className="text-xs">
                            {template.category.label}
                          </Badge>
                        )}
                        <span className="text-xs text-gray-400">
                          {new Date(template.updatedAt).toLocaleDateString()}
                        </span>
                        {!template.isActive && (
                          <Badge
                            variant="outline"
                            className="text-xs text-amber-700 border-amber-200 bg-amber-50"
                          >
                            Archived
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(template)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {template.isActive ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setArchiveConfirmId(template.id)}
                          title="Archive"
                      >
                        <Archive className="w-4 h-4 text-gray-500" />
                      </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnarchive(template.id)}
                          title="Unarchive"
                        >
                          <RotateCcw className="w-4 h-4 text-gray-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <p className="mb-4">You haven't created any prompts yet.</p>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Prompt
            </Button>
          </div>
        )}
      </div>

      {templatesData?.items && templatesData.items.length > 0 && (
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-2" />
            New Prompt
          </Button>
        </DialogFooter>
      )}
    </>
  );

  const renderForm = () => (
    <>
      <DialogHeader>
        <DialogTitle>
          {mode === 'create' ? 'Create New Prompt' : 'Edit Prompt'}
        </DialogTitle>
      </DialogHeader>

      <div className="py-4 space-y-4">
        <div>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="e.g., Welcome Email Template"
            className="mt-1.5"
          />
        </div>

        <div>
          <Label htmlFor="description">Description (optional)</Label>
          <Input
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Brief description of when to use this prompt"
            className="mt-1.5"
          />
        </div>

        <div>
          <Label htmlFor="category">Category</Label>
          <Select
            value={formData.categoryId?.toString() || ''}
            onValueChange={(value) => setFormData(prev => ({ 
              ...prev, 
              categoryId: value ? parseInt(value) : null 
            }))}
          >
            <SelectTrigger className="mt-1.5">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {categoriesData?.map((cat: { id: number; label: string }) => (
                <SelectItem key={cat.id} value={cat.id.toString()}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label htmlFor="bodyTemplate">Prompt Body</Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowPreview(!showPreview)}
                className="h-7 text-xs"
              >
                <Eye className="w-3.5 h-3.5 mr-1" />
                {showPreview ? 'Hide Preview' : 'Preview'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowVariables(!showVariables)}
                className="h-7 text-xs"
              >
                <ChevronDown className={cn(
                  "w-3.5 h-3.5 mr-1 transition-transform",
                  showVariables && "rotate-180"
                )} />
                Variables
              </Button>
            </div>
          </div>
          
          {showVariables && variablesData && (
            <div className="mb-2 p-3 bg-gray-50 rounded-lg border">
              <p className="text-sm text-gray-600 mb-2">
                Click a variable to insert it into your prompt:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {variablesData.variables.map((v: PromptVariable) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVariable(v.key)}
                    className="px-2 py-1 text-xs bg-white border rounded hover:bg-purple-50 hover:border-purple-200 transition-colors"
                    title={v.description || v.label}
                  >
                    {`{{${v.key}}}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Textarea
            id="bodyTemplate"
            value={formData.bodyTemplate}
            onChange={(e) => setFormData(prev => ({ ...prev, bodyTemplate: e.target.value }))}
            placeholder="Write your prompt here. Use {{variableKey}} to insert dynamic values like {{advisorName}} or {{businessName}}."
            className="min-h-[200px] font-mono text-sm"
          />

          {showPreview && formData.bodyTemplate && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
              <p className="text-sm text-gray-500 mb-2">Preview with your profile values:</p>
              <div className="text-sm text-gray-700 whitespace-pre-wrap">
                {substituteVariables(formData.bodyTemplate, profileValues)}
              </div>
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => {
            setMode('list');
            setEditingTemplate(null);
            setFormData(initialFormData);
          }}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {mode === 'create' ? 'Create Prompt' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          {mode === 'list' ? renderList() : renderForm()}
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation */}
      <AlertDialog open={archiveConfirmId !== null} onOpenChange={() => setArchiveConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this prompt?</AlertDialogTitle>
            <AlertDialogDescription>
              This prompt will be archived and hidden from your prompt library.
              You can view archived prompts in your account settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => archiveConfirmId && handleArchive(archiveConfirmId)}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default MyPromptsDialog;

